import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  licenseIsInfinite,
  renderLicenseCredits,
  renderLicenseExtrapack,
} from "./LicenceCredits";
import { LicenseStatus } from "../../redux/actions/licenses/licenceTypes";
import { unlinkLicenseUser } from "../../redux/actions/licenses/licenseListAsyncActions";
import { ThunkDispatch } from "redux-thunk";
import { GridDeleteIcon } from "@mui/x-data-grid-premium";
import { Add } from "@mui/icons-material";
import { TFunction } from "i18next";
import {
  Box,
  Button,
  Chip,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  focusedLicense,
  LicensesFilters,
  selectLicencesAndOwners,
  setLicenseId,
  setPreselectedFilterField,
  setShowFilterForm,
} from "../../redux/slices/licenses/licenseList";

import {
  FirstLetterUpperCase,
  getOperatorsFromAvailableFilters,
} from "../helpers/helpersSearchs";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";

const UnlinkConfirmButton: FC<{
  disabled: boolean;
  onConfirm: () => Promise<any>;
  t: TFunction;
}> = ({ disabled, onConfirm, t }) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <Button
        disabled={disabled}
        color="error"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={LicenseStylesColums.buttonTextTransform}
      >
        <GridDeleteIcon />
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={LicenseStylesColums.popoverBox}>
          <Typography
            variant="body2"
            sx={LicenseStylesColums.popoverTypography}
          >
            {t("licenses.unlinkUser_confirm")}
          </Typography>
          <Box sx={LicenseStylesColums.popoverActionsBox}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setAnchor(null)}
              sx={LicenseStylesColums.buttonTextTransform}
            >
              {t("Common_options.Cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              sx={LicenseStylesColums.buttonTextTransform}
              onClick={() => {
                setAnchor(null);
                onConfirm();
              }}
            >
              {FirstLetterUpperCase(t("Common_options.confirm"))}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const useSetActions = () => {
  const dispatch = useDispatch();
  return {
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField("siteName"));
    },

    setLicenseId: (licenseId: string) => {
      dispatch(setLicenseId(licenseId));
    },
  };
};

const recogniceLicenseStatus = (
  expiryDate: string | null,
  activationDate: string | null,
  username: string | null,
  t: TFunction,
): { status: string; color: string; bg: string } | null => {
  if (!expiryDate) return null;

  const now = new Date();
  const expiry = new Date(expiryDate);
  const activation = activationDate ? new Date(activationDate) : null;

  if (!username)
    return {
      status: t("licenses.unassigned"),
      color: LicenseStylesColums.statusColors.unassigned.color,
      bg: LicenseStylesColums.statusColors.unassigned.bg,
    };
  if (expiry <= now)
    return {
      status: t("licenses.expired"),
      color: LicenseStylesColums.statusColors.expired.color,
      bg: LicenseStylesColums.statusColors.expired.bg,
    };
  if (activation && activation > now)
    return {
      status: t("licenses.upcoming"),
      color: LicenseStylesColums.statusColors.upcoming.color,
      bg: LicenseStylesColums.statusColors.upcoming.bg,
    };
  return {
    status: t("licenses.active"),
    color: LicenseStylesColums.statusColors.active.color,
    bg: LicenseStylesColums.statusColors.active.bg,
  };
};

export const useLicenseColumns = (
  t: TFunction,
  dispatch: ThunkDispatch<any, any, any>,
  tableOptions?: any[],
) => {
  const { onOpenFilter } = useSetActions();
  const { i18n } = useTranslation();
  const { filterModel } = useSelector(LicensesFilters);
  const licenses = useSelector(selectLicencesAndOwners);
  const [sortAlpha, setSortAlpha] = useState<"asc" | "desc" | null>(null);
  const [sortSite, setSortSite] = useState<"asc" | "desc" | null>(null);
  const [activeSort, setActiveSort] = useState<"username" | "siteName" | null>(
    null,
  );

  const toggleAlphaSort = (
    _field: string,
    direction: "asc" | "desc" | null,
  ) => {
    if (direction === null) {
      setActiveSort(null);
      setSortAlpha(null);
      return;
    }
    setActiveSort("username");
    setSortSite(null);
    setSortAlpha(direction);
  };

  const toggleSiteSort = (_field: string, direction: "asc" | "desc" | null) => {
    if (direction === null) {
      setActiveSort(null);
      setSortSite(null);
      return;
    }
    setActiveSort("siteName");
    setSortAlpha(null);
    setSortSite(direction);
  };

  let licenseColumns: any[] = [
    {
      field: "siteName",
      headerName: t("Dashboard.activityTable.site"),
      disableColumnMenu: true,
      width: 255,
      sortable: false,
      type: "string",
      filterOperators: getOperatorsFromAvailableFilters([
        "$ilike",
        "$eq",
        "$ne",
      ]),
      filter: "LOCAL",
      service: "text",
      renderHeader: () => (
        <CustomGeneralColumnHeader
          headerName={t("Dashboard.activityTable.site")}
          field={"siteName"}
          filterModel={filterModel}
          availableFilters={["$ilike", "$eq", "$ne"]}
          onOpenFilter={onOpenFilter}
          newOptions={tableOptions}
          backgroundColor="#fafafa"
          sortable={true}
          sortModel={
            activeSort === "siteName" && sortSite
              ? [{ field: "siteName", sort: sortSite }]
              : []
          }
          onSort={toggleSiteSort}
        />
      ),
      renderCell(params) {
        const license = params.row?.license;
        const siteName = params.row?.siteName;
        const username = params.row?.username;

        if (!params.row || !license) {
          return null;
        }

        if (siteName) {
          return <>{siteName}</>;
        }

        if (license.ownerGroupId && !username) {
          return <Skeleton variant="text" width="100%" height={40} />;
        }

        return null;
      },
    },
    {
      field: "licenseStatus",
      headerName: t("licenses.licensesStatus"),
      disableColumnMenu: true,
      width: 155,
      sortable: false,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        const status = recogniceLicenseStatus(
          license.expiryDate ?? null,
          license.activationDate ?? null,
          license.username ?? null,
          t,
        );

        if (license?.ownerGroupId && !license?.username) return <div />;

        return (
          <div
            style={{
              ...LicenseStylesColums.statusBadge,
              color: status?.color,
              backgroundColor: status?.bg,
            }}
          >
            <span
              className="dot"
              style={{
                ...LicenseStylesColums.statusDot,
                backgroundColor: status?.color,
              }}
            ></span>
            {status?.status}
          </div>
        );
      },
    },
    {
      field: "balance",
      headerName: t("licenses.balance"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        return renderLicenseCredits(
          [params.row.license],
          t,
          t("licenses.addLicense"),
          t("licenses.expired"),
        );
      },
    },
    {
      field: "extra",
      headerName: t("licenses.extra_pack"),
      disableColumnMenu: true,
      width: 190,
      sortable: false,
      type: "string",

      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        return (
          <Tooltip title={t("licenses.extra_pack_desc")} placement="top" arrow>
            <span>
              {renderLicenseExtrapack(
                [params.row.license],
                t("licenses.addLicense"),
                t("licenses.expired"),
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "totalCreditsSinceStart",
      headerName: t("licenses.totalCreditsSinceStart"),
      disableColumnMenu: true,
      width: 180,
      sortable: true,
      renderCell(params) {
        if (!params.row?.license) return null;
        if (params.row?.license?.ownerGroupId && !params.row?.license?.username)
          return <div />;
        return params.value ?? null;
      },
    },
    {
      field: "activationDate",
      headerName: t("licenses.activation"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        const activationValue = params.value ?? license?.activationDate ?? null;
        if (!activationValue || !license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const activationDate = new Date(activationValue);
        return <span>{activationDate.toLocaleDateString(i18n.language)}</span>;
      },
    },
    {
      field: "expiryDate",
      headerName: t("licenses.expiration"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        const expiryValue = params.value ?? license?.expiryDate;
        if (!expiryValue || !license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;

        const oneMonthFromNow = new Date();
        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        const isExpiringSoon = !isExpired && expiryDate < oneMonthFromNow;
        const daysLeft = Math.ceil(
          (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        );
        return (
          <>
            {(isExpired || isExpiringSoon) && (
              <Tooltip
                title={
                  isExpired
                    ? FirstLetterUpperCase(t("licenses.expired"))
                    : t("licenses.expiresDays", { count: daysLeft })
                }
              >
                <Chip
                  style={{
                    ...LicenseStylesColums.expiryChip,
                    backgroundColor: isExpired
                      ? "transparent"
                      : LicenseStylesColums.statusColors.expired.bg,
                  }}
                  variant="filled"
                />
              </Tooltip>
            )}
            <span>{expiryDate.toLocaleDateString(i18n.language)}</span>
          </>
        );
      },
    },

    {
      field: "",
      headerName: t("licenses.addPack"),
      disableColumnMenu: true,
      width: 100,
      sortable: false,
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const expiryValue = license?.expiryDate;
        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        return licenseIsInfinite(params.row.license) ||
          !(params.row.license as any).hasPacks ? null : (
          <Button
            color="info"
            disabled={isExpired}
            onClick={() => dispatch(focusedLicense(params.row.id))}
          >
            <Add />
          </Button>
        );
      },
    },
    {
      field: "id",
      headerName: t("licenses.unlinkUser"),
      disableColumnMenu: true,
      width: 120,
      sortable: false,
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const expiryValue = license?.expiryDate;
        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        if (!params.row?.license) return null;
        return (
          <UnlinkConfirmButton
            disabled={
              params.row.license.statusConstantId === LicenseStatus.expired ||
              !params.row.username ||
              isExpired
            }
            onConfirm={() =>
              (dispatch(unlinkLicenseUser(params.value)) as any).unwrap()
            }
            t={t}
          />
        );
      },
    },
  ];

  let licensesRows: any[] = licenses
    .map((license) => {
      const isUnassigned = !license?.username;
      const usernameKey = license?.username ?? `unassigned_${license.id}`;
      const rows = {
        id: license.id,
        path: isUnassigned ? [String(license.id)] : [usernameKey, license.id],
        username: license?.username,
        siteName: license?.siteName,
        licenseStatus: recogniceLicenseStatus(
          license.expiryDate ?? null,
          license.activationDate ?? null,
          license?.username ?? null,
          t,
        ),
        activationDate: license.activationDate,
        balance: license.availableCredits,
        expiryDate: license.expiryDate,
        license: license,
        totalCreditsSinceStart: license?.metadata?.totalCreditsSinceStart
          ? license.metadata.totalCreditsSinceStart / 1000
          : "",
      };
      return rows;
    })
    .sort((a, b) => {
      const aUnassigned = !a.username;
      const bUnassigned = !b.username;
      if (aUnassigned && !bUnassigned) return 1;
      if (!aUnassigned && bUnassigned) return -1;

      if (activeSort === "siteName" && sortSite) {
        const aKey = (a.siteName ?? "").toLowerCase();
        const bKey = (b.siteName ?? "").toLowerCase();
        return sortSite === "asc"
          ? aKey.localeCompare(bKey)
          : bKey.localeCompare(aKey);
      }

      if (activeSort === "username" && sortAlpha) {
        const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
        const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
        return sortAlpha === "asc"
          ? aKey.localeCompare(bKey)
          : bKey.localeCompare(aKey);
      }

      const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
      const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
      return aKey.localeCompare(bKey);
    });

  return {
    licenseColumns,
    licensesRows,
    sortAlpha,
    toggleAlphaSort,
    activeSort,
  };
};
